import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Form, Input, Modal, Space, Tag, Typography, message } from 'antd';
import { errorMessageWithFieldErrors, formatDateTime, space, useCapability } from '@viq-admin/ui';
import { getZohoApp, putZohoApp, type ZohoAppSaveBody } from '../../api/crmApps';

/**
 * VendoIQ's own Zoho OAuth app credentials (viq-backend spec 2026-11-02,
 * "Zoho app credentials, managed in the console"). Customers never see this:
 * they connect their own Zoho from the portal's Integrations page.
 *
 * Anyone with `read Integration` sees the status; Set up / Edit needs
 * `manage:Integration` (the same capability as Create/Delete on this page).
 * The secret is write-only: the editor always opens with it empty.
 */
const ZOHO_APP_QUERY_KEY = ['crmApp', 'zoho'] as const;

const trim = (v: unknown) => (typeof v === 'string' ? v.trim() : v);

const FIELD_RULES = [
  { required: true, message: 'Required' },
  { max: 200, message: 'At most 200 characters' },
  { pattern: /^\S+$/, message: 'Must not contain spaces' },
];

export function ZohoAppCard() {
  const canManage = useCapability('manage:Integration');
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ZOHO_APP_QUERY_KEY, queryFn: getZohoApp });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<ZohoAppSaveBody>();

  function openEditor() {
    form.setFieldsValue({ clientId: status.data?.clientId ?? '', clientSecret: '' });
    setOpen(true);
  }

  async function save(values: ZohoAppSaveBody) {
    setSaving(true);
    try {
      await putZohoApp(values);
      await queryClient.invalidateQueries({ queryKey: ZOHO_APP_QUERY_KEY });
      message.success('Zoho app credentials saved.');
      setOpen(false);
    } catch (err: unknown) {
      const fieldErrors = (err as { errors?: Record<string, string[]> } | null)?.errors;
      if (fieldErrors) {
        form.setFields(
          Object.entries(fieldErrors).map(([name, errors]) => ({
            name: name as keyof ZohoAppSaveBody,
            errors,
          })),
        );
      }
      message.error(errorMessageWithFieldErrors(err, 'Couldn’t save the Zoho app credentials.'));
    } finally {
      setSaving(false);
    }
  }

  const data = status.data;
  const action =
    canManage && data && !status.isError ? (
      <Button onClick={openEditor}>{data.configured ? 'Edit' : 'Set up'}</Button>
    ) : null;

  return (
    <Card
      title="Zoho app"
      size="small"
      extra={action}
      data-testid="zoho-app-card"
      style={{ marginBottom: space.lg }}
    >
      {status.isLoading ? (
        <Typography.Text type="secondary">Loading…</Typography.Text>
      ) : status.isError ? (
        <Space direction="vertical">
          <Alert type="error" showIcon message="Couldn’t load the Zoho app status" />
          <Button loading={status.isFetching} onClick={() => void status.refetch()}>Retry</Button>
        </Space>
      ) : data ? (
        <Space direction="vertical" size="small">
          <Space wrap>
            {data.configured ? <Tag color="green">Configured</Tag> : <Tag>Not configured</Tag>}
            {data.configured && data.clientId ? (
              <Typography.Text code>{data.clientId}</Typography.Text>
            ) : null}
            {data.updatedAt ? (
              <Typography.Text type="secondary">
                Updated {formatDateTime(data.updatedAt)}
              </Typography.Text>
            ) : null}
          </Space>
          <Typography.Text type="secondary">
            VendoIQ’s Zoho app, from Zoho’s API Console. Customers connect their own Zoho from
            the portal.
          </Typography.Text>
        </Space>
      ) : null}

      <Modal
        title="Zoho app credentials"
        open={open}
        okText="Save"
        confirmLoading={saving}
        onOk={() => form.submit()}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        {data?.configured ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: space.md }}
            message="Changing the Client ID disconnects every customer’s Zoho until they reconnect. Saving a new secret for the same Client ID is safe."
          />
        ) : null}
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          <Form.Item name="clientId" label="Client ID" rules={FIELD_RULES} normalize={trim}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="clientSecret"
            label="Client Secret"
            rules={FIELD_RULES}
            normalize={trim}
            extra="Paste it from Zoho’s API Console. It’s stored in Key Vault and never shown again."
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
